/**
 * §P.7 · El club: sus datos, sus sedes, sus disciplinas y su personal.
 *
 * ## Tres cosas de aquí no son papeleo
 *
 * **`legal_name` y `tax_id` salen impresos en los recibos.** Un club que emite
 * doscientos al mes con el NIF mal puesto tiene un problema con Hacienda, no con la
 * aplicación. La pantalla lo dice donde se editan.
 *
 * **`timezone` manda sobre todo lo que tenga hora**: el horario de silencio, la
 * generación de sesiones y el vencimiento de la remesa. Cambiarla mueve las tres.
 *
 * **Las coordenadas de una sede puestas a mano son para siempre.**
 * `directory.geocode_venues` nunca sobrescribe un punto con `geocode_source:
 * 'manual'`, porque si el maestro lo movió sabe más que el geocodificador — él sabe
 * que la entrada está por el callejón de atrás—. Es la única ruta del backend que
 * lo hace, y por eso la pantalla avisa antes.
 */

export interface Club {
  readonly id: string;
  readonly slug: string;
  readonly legal_name: string | null;
  readonly trade_name: string;
  readonly tax_id: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly website: string | null;
  readonly country: string;
  /** **Manda sobre todo lo que tenga hora.** Ver la cabecera. */
  readonly timezone: string;
  readonly locale: string;
  readonly currency: string;
  readonly status: string;
  readonly onboarded_at: Date | string | null;
  readonly counts: {
    readonly active_students: number;
    readonly venues: number;
    readonly staff: number;
  };
}

/**
 * Cómo de fino está el punto de una sede.
 *
 * Importa porque **una sede situada a nivel de ciudad cae en el centro del
 * municipio**, y decirle a una madre que ese gimnasio está «a 400 m» cuando está a
 * cuatro kilómetros quema el directorio entero.
 */
export type PrecisionDeGeocodificacion =
  'exacta' | 'calle' | 'codigo_postal' | 'ciudad';

export const PRECISIONES: Record<
  string,
  { etiqueta: string; clase: string; explicacion: string }
> = {
  exacta: {
    etiqueta: 'Exacta',
    clase: 'pastilla-bien',
    explicacion: 'El punto está en el portal.',
  },
  calle: {
    etiqueta: 'A nivel de calle',
    clase: 'pastilla-info',
    explicacion: 'Sirve para buscar por cercanía.',
  },
  codigo_postal: {
    etiqueta: 'Solo el código postal',
    clase: 'pastilla-paused',
    explicacion:
      'El punto cae en el centro del código postal, así que las distancias que ve una familia pueden estar a un kilómetro.',
  },
  ciudad: {
    etiqueta: 'Solo la ciudad',
    clase: 'pastilla-mal',
    explicacion:
      'El punto cae en el centro del municipio. Una familia puede ver «a 400 m» estando a cuatro kilómetros: conviene moverlo a mano.',
  },
};

export interface Sede {
  readonly id: string;
  readonly name: string;
  readonly address_line: string | null;
  readonly postal_code: string | null;
  readonly city: string | null;
  readonly province: string | null;
  readonly country: string;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly geocoded_at: Date | string | null;
  readonly geocode_precision: PrecisionDeGeocodificacion | null;
  /** `manual` es el que importa: el geocodificador ya no lo toca. */
  readonly geocode_source: string | null;
  readonly timezone: string;
  readonly is_primary: boolean;
  readonly active: boolean;
  readonly group_count: number;
}

export interface DisciplinaDelClub {
  readonly discipline_id: string;
  readonly code: string;
  readonly name: string;
  readonly belt_scheme_id: string | null;
  readonly belt_scheme_name: string | null;
  /** Grupos activos que la imparten. Es lo que impide quitarla a la ligera. */
  readonly group_count: number;
}

export type RolDePersonal = 'admin' | 'coach' | 'assistant';

export const ROLES_DE_PERSONAL: readonly {
  valor: RolDePersonal;
  etiqueta: string;
  explicacion: string;
}[] = [
  {
    valor: 'admin',
    etiqueta: 'Administrativo',
    explicacion:
      'Todo menos invitar personal: alumnos, grupos, cobros y avisos.',
  },
  {
    valor: 'coach',
    etiqueta: 'Entrenador',
    explicacion:
      'Sus grupos, pasar lista, grados y avisos. **No ve el dinero de nadie.**',
  },
  {
    valor: 'assistant',
    etiqueta: 'Ayudante',
    explicacion:
      'Pasar lista y poco más. Ni dinero, ni ficha médica, ni contactos.',
  },
];

export const etiquetaDeRol = (rol: string): string =>
  ({
    owner: 'Dueño',
    admin: 'Administrativo',
    coach: 'Entrenador',
    assistant: 'Ayudante',
  })[rol] ?? rol;

export type EstadoDePersonal = 'invited' | 'active' | 'suspended';

export const etiquetaDeEstadoDePersonal = (estado: string): string =>
  ({
    invited: 'Invitado, sin aceptar',
    active: 'Activo',
    suspended: 'Suspendido',
  })[estado] ?? estado;

export interface MiembroDelPersonal {
  readonly membership_id: string;
  readonly user_id: string;
  readonly name: string;
  readonly email: string;
  readonly role: string;
  readonly status: EstadoDePersonal;
  readonly invited_at: Date | string | null;
  readonly accepted_at: Date | string | null;
  /** **Vacío significa todas las sedes**, no ninguna. Ver la cabecera del DTO. */
  readonly venues: readonly { id: string; name: string }[];
}

/** Lo que devuelve invitar: el miembro y si de verdad se mandó un correo. */
export interface ResultadoDeInvitacion {
  readonly member: MiembroDelPersonal;
  /**
   * `false` cuando la persona ya estaba dentro.
   *
   * Decirle «te han invitado a Dojang Pascual» a quien lleva dos años usándolo es
   * cómo un producto parece roto, así que la pantalla enseña una frase distinta
   * según esto.
   */
  readonly invitation_sent: boolean;
}

/**
 * La dirección de una sede, en una línea.
 *
 * Sin los huecos de los campos que faltan: «Calle Mayor 3, , Alcalá» se lee como un
 * dato roto, y una sede recién creada casi nunca tiene provincia.
 */
export function direccionDeSede(s: Sede): string {
  return [s.address_line, s.postal_code, s.city, s.province]
    .map((x) => x?.trim())
    .filter((x) => x && x.length > 0)
    .join(', ');
}

/** Si el punto del mapa lo movió una persona. El geocodificador ya no lo toca. */
export const puntoPuestoAMano = (s: Sede): boolean =>
  s.geocode_source === 'manual';

/** Si tiene punto en el mapa. Sin los dos números no sitúa nada. */
export const tienePunto = (s: Sede): boolean =>
  s.latitude !== null && s.longitude !== null;

/**
 * Una coordenada tecleada → número, o `null` si el campo está vacío.
 *
 * `undefined` es «no se entiende», que no es lo mismo que «sin coordenada»: lo
 * primero es un error de tecleo y lo segundo es dejar que el geocodificador la
 * ponga. Colapsarlos borraría un punto bueno por una coma mal puesta.
 *
 * **`Number` y no `Number.parseFloat`**, y esto lo encontró una prueba: `parseFloat`
 * lee lo que puede y se calla el resto, así que `40,48,16` le sale `40.48` y
 * `40 grados` le sale `40`. Eso no es «no se entiende», es peor: es un punto
 * plausible, a kilómetros del bueno, guardado sin que nadie se entere. `Number`
 * exige que el texto entero sea un número.
 */
export function aCoordenada(
  texto: string,
  tope: number
): number | null | undefined {
  const limpio = texto.trim().replace(',', '.');
  if (limpio === '') return null;

  const n = Number(limpio);
  if (!Number.isFinite(n) || n < -tope || n > tope) return undefined;
  return n;
}

/**
 * Las dos coordenadas van juntas o ninguna.
 *
 * El servidor responde `422 incomplete_coordinates` a media coordenada, y con
 * razón: no sitúa nada y rompe la búsqueda por cercanía. Se comprueba aquí para no
 * mandar un formulario que se sabe que va a fallar.
 */
export const coordenadasCompletas = (
  lat: number | null,
  lon: number | null
): boolean => (lat === null) === (lon === null);

/* ── §P.7 fase 2 · La marca, los interruptores y los papeles del personal ── */

/** Lo que el servidor resolvió sobre un color: **resuelve, no rechaza**. */
export interface Contraste {
  /** El color de texto que sí se lee encima. Lo decide el servidor. */
  readonly text_color: string;
  readonly ratio_on_white_text: number;
  readonly ratio_on_dark_text: number;
  readonly meets_aa_with_white_text: boolean;
}

export interface MarcaDelClub {
  readonly brand_color: string | null;
  readonly accent_color: string | null;
  readonly preset_code: string | null;
  readonly logo_asset_id: string | null;
  readonly logo_dark_asset_id: string | null;
  /** De solo lectura aquí: es único en la plataforma y se cambia desde §A. */
  readonly custom_domain: string | null;
  readonly hide_kiap_branding: boolean;
  readonly brand_contrast: Contraste | null;
  readonly accent_contrast: Contraste | null;
  readonly updated_at: Date | string | null;
}

/**
 * Los interruptores del club. **Lista cerrada** (H-5).
 *
 * Viven en `academies.settings jsonb`, que `DDBB.md` §10 define como *preferencias de
 * interfaz, no reglas de cobro*: ninguna clave que decida cuánto se cobra puede estar
 * aquí. Eso son columnas con tipo y con `CHECK`.
 */
export interface AjustesDelClub {
  readonly internal_ranking: boolean;
  readonly birthdays: boolean;
  readonly second_hand: boolean;
  readonly reminders: {
    readonly session: boolean;
    readonly exam: boolean;
    readonly payment: boolean;
  };
  /** **El único que no va de comodidad sino de daño.** Ver `INTERRUPTORES`. */
  readonly weight_logs_under_16: boolean;
}

export type ClaseDeDocumento =
  | 'certificado_delitos'
  | 'titulacion'
  | 'contrato'
  | 'licencia'
  | 'seguro'
  | 'otro';

export const CLASES_DE_DOCUMENTO: readonly {
  valor: ClaseDeDocumento;
  etiqueta: string;
  /** El que es obligatorio por ley. Ver `DocumentoDePersonal`. */
  obligatorio?: true;
}[] = [
  {
    valor: 'certificado_delitos',
    etiqueta: 'Certificado de delitos sexuales',
    obligatorio: true,
  },
  { valor: 'titulacion', etiqueta: 'Titulación' },
  { valor: 'contrato', etiqueta: 'Contrato' },
  { valor: 'licencia', etiqueta: 'Licencia federativa' },
  { valor: 'seguro', etiqueta: 'Seguro' },
  { valor: 'otro', etiqueta: 'Otro' },
];

export const etiquetaDeClaseDeDocumento = (clase: string): string =>
  CLASES_DE_DOCUMENTO.find((c) => c.valor === clase)?.etiqueta ?? clase;

/**
 * Un papel del personal.
 *
 * El que importa es `certificado_delitos`: **obligatorio por ley** para todo el que
 * trata con menores, y el club responde de tenerlo al día. Y lo que aporta el
 * producto no es guardar el papel —eso lo hace una carpeta— sino **avisar antes de
 * que caduque**, que es lo que nadie hace a mano y lo que deja a un club con personal
 * dando clase con el certificado vencido.
 */
export interface DocumentoDePersonal {
  readonly id: string;
  readonly user_id: string;
  readonly name: string | null;
  readonly kind: string;
  readonly reference: string | null;
  readonly issued_on: Date | string | null;
  readonly expires_on: Date | string | null;
  /** Siempre un PDF. `null` hasta que §S.3 permita subirlo desde el panel. */
  readonly asset_id: string | null;
  readonly verified_at: Date | string | null;
  readonly verified_by: string | null;
  /** Negativo si ya caducó. `null` si no caduca. */
  readonly days_to_expiry: number | null;
}

/**
 * En qué estado está un papel respecto a su caducidad.
 *
 * `sin_caducidad` **no es «al día»**: un certificado sin fecha de caducidad puesta es
 * un certificado del que no sabemos nada, y pintarlo en verde es exactamente cómo un
 * club se queda con personal dando clase con el papel vencido creyendo que está bien.
 */
export type EstadoDeDocumento =
  'caducado' | 'caduca_pronto' | 'al_dia' | 'sin_caducidad';

/** Un mes de margen: lo que tarda una administración en renovar un certificado. */
export const DIAS_DE_AVISO = 30;

export function estadoDeDocumento(d: {
  days_to_expiry: number | null;
}): EstadoDeDocumento {
  if (d.days_to_expiry === null) return 'sin_caducidad';
  if (d.days_to_expiry < 0) return 'caducado';
  return d.days_to_expiry <= DIAS_DE_AVISO ? 'caduca_pronto' : 'al_dia';
}

export const ESTADOS_DE_DOCUMENTO: Record<
  EstadoDeDocumento,
  { etiqueta: string; clase: string }
> = {
  caducado: { etiqueta: 'Caducado', clase: 'pastilla-mal' },
  caduca_pronto: { etiqueta: 'Caduca pronto', clase: 'pastilla-paused' },
  al_dia: { etiqueta: 'Al día', clase: 'pastilla-bien' },
  // Ni verde ni rojo: no sabemos. Ver `EstadoDeDocumento`.
  sin_caducidad: { etiqueta: 'Sin fecha de caducidad', clase: 'pastilla' },
};

/**
 * Cuánto queda, en palabras.
 *
 * «Caducó hace 12 días» y «caduca en 12 días» son dos frases distintas que salen del
 * mismo número con distinto signo, y confundirlas es decirle a un club que tiene
 * tiempo cuando ya no lo tiene.
 */
export function cuantoQuedaDeDocumento(dias: number | null): string {
  if (dias === null) return 'No caduca, o no se ha puesto la fecha';
  if (dias < 0) {
    const pasados = Math.abs(dias);
    return pasados === 1 ? 'Caducó ayer' : `Caducó hace ${pasados} días`;
  }
  if (dias === 0) return 'Caduca hoy';
  return dias === 1 ? 'Caduca mañana' : `Caduca en ${dias} días`;
}
